import { Button, IconButton } from '@material-ui/core';
import { CornerDownRight, PlusCircle, XCircle } from 'lucide-react';
import React from 'react';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';

/**
 * Shared layout shell for the automation "two-value mapping" editors — the
 * repeatable `source → target` rows with a per-row remove and an "Add" button.
 * Both the Sync-to-Data-Destination field mapper and the Enroll-in-Experience
 * supporting-actor mapper render through this so they stay visually consistent
 * (the stacked source-on-top / target-below layout); each supplies its own row
 * cells (dropdown, autocomplete, free text) via the `renderSource` /
 * `renderTarget` render props.
 */
export function MappingRows<T>({
  rows,
  onChange,
  renderSource,
  renderTarget,
  createEmptyRow,
  addLabel,
  canAdd = true,
  header,
  separator
}: {
  rows: T[];
  onChange: (next: T[]) => void;
  renderSource: (
    row: T,
    update: (next: T) => void,
    index: number
  ) => React.ReactNode;
  renderTarget: (
    row: T,
    update: (next: T) => void,
    index: number
  ) => React.ReactNode;
  createEmptyRow: () => T;
  addLabel: string;
  /** Hide the "Add" button when the caller has no more rows to offer. */
  canAdd?: boolean;
  header?: React.ReactNode;
  /** Element rendered before the target cell (defaults to a corner-down arrow). */
  separator?: React.ReactNode;
}) {
  const { t } = useUnsafeTranslation();

  const updateAt = (index: number, next: T) =>
    onChange(rows.map((r, i) => (i === index ? next : r)));
  const removeAt = (index: number) =>
    onChange(rows.filter((_, i) => i !== index));

  return (
    <FlexContainerVertical spacing={2}>
      {header}
      {rows.map((row, i) => (
        <FlexContainerVertical key={i} spacing={1.5} marginTop={0.5}>
          <FlexContainer spacing={1} fullWidth>
            <div style={{ flex: 1 }}>
              {renderSource(row, (next) => updateAt(i, next), i)}
            </div>
            <IconButton
              aria-label={t('Remove mapping')}
              onClick={() => removeAt(i)}
            >
              <XCircle size={16} />
            </IconButton>
          </FlexContainer>
          <FlexContainer spacing={1} fullWidth>
            {separator ?? <CornerDownRight size={16} />}
            <div style={{ flex: 1 }}>
              {renderTarget(row, (next) => updateAt(i, next), i)}
            </div>
          </FlexContainer>
        </FlexContainerVertical>
      ))}
      {canAdd && (
        <Button
          size="small"
          color="primary"
          startIcon={<PlusCircle size={20} />}
          onClick={() => onChange([...rows, createEmptyRow()])}
        >
          {addLabel}
        </Button>
      )}
    </FlexContainerVertical>
  );
}
